// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CarouselStyles } from "@insite/content-library/Components/Carousel";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Clickable from "@insite/mobius/Clickable";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getTextAlignmentField, { textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import FlexWrapper, { FlexWrapperPresentationProps } from "@store/Library/Components/Toolkit/FlexWrapper";
import Carousel from "@store/Overrides/Components/Carousel";
import { carouselProps } from "@store/Themes/common/props/carousel";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { createRef, FC, useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    images = "images",
    maximumNumberOfImagesPerRow = "maximumNumberOfImagesPerRow",
    maximumImageHeight = "maximumImageHeight",
    showCarousel = "showCarousel",
    showDots = "showDots",
    title = "title",
    titleAlignment = "titleAlignment",
}

const titleAlignmentField = getTextAlignmentField(fields.titleAlignment);

const enum imageFields {
    altText = "altText",
    id = "id",
    link = "link",
    path = "path",
}

interface ImageStyleProps {
    style: {
        width: string;
        maxWidth: string;
        maxHeight: string;
    };
}

interface ImageProps {
    image: {
        altText?: string;
        id: string;
        link?: LinkFieldValue;
        path: string;
    };
    imageStyleProps?: ImageStyleProps;
}

interface ImagesFields {
    fields: {
        [imageFields.altText]: string;
        [imageFields.id]: string;
        [imageFields.link]: LinkFieldValue;
        [imageFields.path]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.images]: ImagesFields[];
        [fields.maximumNumberOfImagesPerRow]: string;
        [fields.maximumImageHeight]: number;
        [fields.showCarousel]: boolean;
        [fields.showDots]: boolean;
        [fields.titleAlignment]: string;
    };
}

export interface ImageGalleryStyles {
    titleText?: TypographyProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    flexWrapper?: FlexWrapperPresentationProps;
    carousel?: CarouselStyles;
    link?: LinkProps;
    image?: LazyImageProps;
}

const imageGalleryStyles: ImageGalleryStyles = {
    titleText: carouselProps.titleText,
    carousel: {
        carouselWrapper: {
            css: css`
                padding: ${pxToRem(0, 20)};
            `,
        },
    },
    flexWrapper: {
        gap: pxToRem(30),
        extendedStyles: {
            content: {
                forwardAs: "ul",
                css: css`
                    justify-content: center;
                `,
            },
            item: {
                forwardAs: "li",
                css: css`
                    justify-content: center;
                `,
            },
        },
    },
    link: {
        css: css`
            flex-grow: 1;
        `,
    },
    image: {
        css: css`
            flex-grow: 1;
        `,
    },
};

const styles = imageGalleryStyles;

const Image: FC<ImageProps> = ({ image, imageStyleProps }) => {
    return image.link?.value ? (
        <Clickable href={image.link.value} {...styles.link}>
            <LazyImage imgProps={imageStyleProps} {...styles.image} src={image.path} altText={image.altText} />
        </Clickable>
    ) : (
        <LazyImage imgProps={imageStyleProps} {...styles.image} src={image.path} altText={image.altText} />
    );
};

const mapImagesToSlides = (images: ImagesFields[], imageStyleProps: ImageStyleProps) =>
    images.map(({ fields: image }, index) => ({
        id: image.id,
        renderComponent: () => {
            return <Image key={image.id || index} image={image} imageStyleProps={imageStyleProps} />;
        },
    }));

const ImageGallery: FC<OwnProps> = ({
    fields: { images, maximumImageHeight, maximumNumberOfImagesPerRow, showCarousel, showDots, title, titleAlignment },
    id,
}) => {
    const afterGallery = createRef<HTMLSpanElement>();
    const [imageStyleProps, setImageStyleProps] = useState<ImageStyleProps>({
        style: {
            width: "",
            maxWidth: "",
            maxHeight: "",
        },
    });

    useEffect(() => {
        setImageStyleProps({
            style: {
                width: "auto",
                maxWidth: "100%",
                maxHeight: `${pxToRem(maximumImageHeight)}`,
            },
        });
    }, [maximumImageHeight]);

    const desktop = parseInt(maximumNumberOfImagesPerRow, 10);
    const tablet = Math.ceil(desktop / 2);
    const mobile = Math.ceil(tablet / 2);

    const titleCss = css`
        ${styles.titleText?.css}
        ${cssProp("text-align", titleAlignment)}
    `;

    return (
        <StyledWrapper {...styles.container}>
            {title ? (
                <Typography variant="headerSecondary" {...styles.titleText} css={titleCss}>
                    {title}
                </Typography>
            ) : null}
            <SkipNav
                extendedStyles={styles.skipToContent}
                text={translate("Skip Image Gallery")}
                destination={afterGallery}
            />
            {showCarousel ? (
                <Carousel
                    id={`image-carousel-${id}`}
                    extendedStyles={styles.carousel}
                    maxNumberOfColumns={Number(maximumNumberOfImagesPerRow)}
                    slides={mapImagesToSlides(images, imageStyleProps)}
                    showDots={showDots}
                    titleAlignment={titleAlignment}
                    useSkipNav={false}
                />
            ) : (
                <FlexWrapper {...styles.flexWrapper} itemsPerRow={[mobile, tablet, tablet, desktop, desktop]}>
                    {images?.map(({ fields: image }, index) => {
                        return <Image key={image.id || index} image={image} imageStyleProps={imageStyleProps} />;
                    })}
                </FlexWrapper>
            )}
            <span ref={afterGallery} tabIndex={-1} />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: ImageGallery,
    definition: {
        group: "Basic",
        icon: "image",
        displayName: "Image Gallery",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
            },
            {
                ...titleAlignmentField.definition,
                displayName: "Title Alignment",
                defaultValue: textAlignmentOptions.center.id,
                fieldType: "Contextual",
                isVisible: x => Boolean(x.fields.title),
            },
            {
                name: fields.showCarousel,
                displayName: "Show Carousel",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.showDots,
                editorTemplate: "CheckboxField",
                isVisible: item => !!item.fields.showCarousel,
                defaultValue: false,
                fieldType: "Contextual",
            },
            {
                name: fields.images,
                displayName: "Select Images",
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: item => item.fields.altText,
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: imageFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: imageFields.path,
                        defaultValue: "",
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                    },
                    {
                        name: imageFields.altText,
                        defaultValue: "",
                        displayName: "Alt Text",
                        editorTemplate: "TextField",
                    },
                    {
                        name: imageFields.link,
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                ],
            },
            {
                name: fields.maximumNumberOfImagesPerRow,
                displayName: "Maximum images per row",
                editorTemplate: "DropDownField",
                defaultValue: "6",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                ],
                fieldType: "Contextual",
                tooltip:
                    "Up to 6 images can display per row. This number should be adjusted based on the number of images you choose to display. For example, if you display 10 images you likely want 5 to display per row. Images will also stack responsively for smaller screen sizes.",
            },
            {
                name: fields.maximumImageHeight,
                displayName: "Maximum image height",
                editorTemplate: "IntegerField",
                defaultValue: 100,
                fieldType: "Contextual",
                tooltip:
                    "By default, images will scale to meet the width of the screen for the number of images to display per row. The maximum height value can be used to scale images to be smaller. Default value is 100 pixels.",
            },
        ],
    },
};

export default widgetModule;
